import { useState } from 'react';
import { BaseControl } from '..';
import { Link, LinkOff } from '@mui/icons-material';
import DraggableNumberInput from '../../components/DraggableNumberInput';
import { CssUnitSelect } from '../../components/CssUnitSelect';

/**
 * @typedef {import('../../components/CssUnitSelect').CssUnit} CssUnit
 */

/**
 * @typedef {object} SpacingValue
 * @property {number} top    - Top spacing value
 * @property {number} right  - Right spacing value
 * @property {number} bottom - Bottom spacing value
 * @property {number} left   - Left spacing value
 * @property {CssUnit} unit - CSS unit for all sides
 */

/**
 * @typedef {object} PaddingMarginOptions
 */

/**
 * @typedef {object} PaddingMarginValue
 * @property {SpacingValue} padding - Padding values per side
 * @property {SpacingValue} margin  - Margin values per side
 */

const SIDES = /** @type {const} */ (
    ['top', 'right', 'bottom', 'left']
);

const SIDE_LABELS = { top: 'Boven', right: 'Rechts', bottom: 'Onder', left: 'Links' };

/**
 * A single spacing section (margin or padding) with four side
 * inputs, a unit selector, and a lock toggle.
 *
 * @param {object} props
 * @param {string} props.label - Section label ('Margin' or 'Padding')
 * @param {SpacingValue} props.value - Current spacing values
 * @param {CssUnit[]} [props.units] - Which units to allow
 * @param {(value: SpacingValue) => void} props.onChange - Change callback
 */
const SpacingSection = ({ 
    label, 
    value, 
    units,
    onChange 
}) => {

    units = units ?? ['px', 'em', '%'];

    const v = value ?? {
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        unit: 'px',
    };

    const [locked, setLocked] = useState((v.top === v.right && v.top === v.bottom && v.top === v.left) || false);

    /**
     * @param {typeof SIDES[number]} side
     * @param {number} value
     */
    const handleSideChange = (side, value) => {
        if (locked) {
            onChange({ ...v, top: value, right: value, bottom: value, left: value });
        } else {
            onChange({ ...v, [side]: value });
        }
    };

    /** @param {CssUnit} unit */
    const handleUnitChange = (unit) => onChange({ ...v, unit });

    return (
        <div className="spacing-section">
            <div className="spacing-section-header">
                <span className="spacing-section-label">
                    {/* <MonitorIcon /> */}
                    {label}
                </span>
                <CssUnitSelect
                    unit={v.unit}
                    onChange={handleUnitChange}
                    units={units}
                />
            </div>

            <div className="spacing-inputs-row">
                <div className="spacing-inputs">
                    {SIDES.map((side) => (
                        <div key={side} className="spacing-input-cell">
                            <DraggableNumberInput
                                className="spacing-input"
                                value={v[side] ?? 0}
                                onChange={(value) => {
                                    handleSideChange(side, value);
                                }}
                            />
                            <span className="spacing-input-label">
                                {SIDE_LABELS[side]}
                            </span>
                        </div>
                    ))}
                </div>

                <button
                    type="button"
                    className={`spacing-lock-btn${locked ? ' locked' : ''}`}
                    onClick={() => {
                        setLocked((l) => !l);
                        
                        /* When locking, set all sides to 0 */
                        if (!locked) {
                            onChange({ ...v, top: 0, right: 0, bottom: 0, left: 0 });
                        }
                    }}
                    title={locked ? 'Unlock sides' : 'Lock all sides together'}
                >
                    {locked ? <Link /> : <LinkOff />}
                </button>
            </div>
        </div>
    );
};

/**
 * @extends {BaseControl<PaddingMarginValue, PaddingMarginOptions>}
 */
export class PaddingMarginControl extends BaseControl {

    /**
     * @param {import('..').BaseControlDefinition<
     *     PaddingMarginValue,
     *     PaddingMarginOptions
     * >} control
     */
    constructor(control) {
        super(control);
        this.children = {};
    }

    /** @type {BaseControl<PaddingMarginValue>['render']} */
    render(value, onChange) {
        const v = value ?? {};
        return (
            <div className="padding-margin-control">
                <SpacingSection
                    label="Marge (buiten)"
                    value={v.margin}
                    onChange={(margin) => onChange({ ...v, margin })}
                />
                <SpacingSection
                    label="Opvulling (binnen)"
                    value={v.padding}
                    onChange={(padding) => onChange({ ...v, padding })}
                />
            </div>
        );
    }
}
